import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const testimonials = [
  {
    name: 'María González',
    role: 'Médica interna, 38 años',
    avatar: 'https://public.readdy.ai/ai/img_res/021432924a1215349b13e7ff00b0e5c9.jpg',
    rating: 5,
    text: 'Llevamos más de 6 meses utilizando HELIX en nuestra consulta como complemento diagnóstico. La precisión en la medición de la frecuencia cardíaca es sorprendente. Mis pacientes lo adoran porque es discreto y nada invasivo.',
    highlight: 'Precisión médica verificada',
  },
  {
    name: 'Alejandro Torres',
    role: 'Triatleta profesional, 31 años',
    avatar: 'https://readdy.ai/api/search-image?query=young%20male%20athlete%20portrait%20headshot%2C%20confident%20smile%2C%20sports%20outfit%2C%20soft%20studio%20lighting%2C%20clean%20white%20background%2C%20realistic%20photo%2C%20early%2030s%20man&width=120&height=120&seq=avatar-02&orientation=squarish',
    rating: 5,
    text: 'Entreno 3 veces al día. HELIX me da los datos de recuperación que antes solo obtenía con equipos de laboratorio. El seguimiento del sueño cambió completamente cómo planifico mis cargas de entrenamiento.',
    highlight: 'Rendimiento deportivo al máximo',
  },
  {
    name: 'Sofía Ramírez',
    role: 'CEO tech startup, 44 años',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20businesswoman%20portrait%20headshot%2C%20elegant%20smile%2C%20business%20attire%2C%20soft%20studio%20lighting%2C%20clean%20background%2C%20realistic%20corporate%20headshot%2C%20mid%2040s%20woman&width=120&height=120&seq=avatar-03&orientation=squarish',
    rating: 5,
    text: 'Tengo el Apple Watch, el Garmin y probé otros anillos inteligentes. HELIX es el único que uso todos los días porque no molesta en reuniones, va con cualquier look y los datos son más fiables.',
    highlight: 'Mejor que cualquier alternativa',
  },
  {
    name: 'David Morales',
    role: 'Fisioterapeuta deportivo, 29 años',
    avatar: 'https://readdy.ai/api/search-image?query=young%20male%20physiotherapist%20portrait%20headshot%2C%20friendly%20smile%2C%20casual%20professional%20attire%2C%20soft%20studio%20lighting%2C%20clean%20background%2C%20realistic%20photo%2C%20late%2020s%20man&width=120&height=120&seq=avatar-04&orientation=squarish',
    rating: 5,
    text: 'Lo recomiendo a todos mis clientes después de lesiones. El monitoreo del sueño y la variabilidad cardíaca son clave en la recuperación. La batería de 7 días es un lujo comparado con otros dispositivos.',
    highlight: 'Indispensable en rehabilitación',
  },
];

const StarRating = ({ count }: { count: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: count }).map((_, i) => (
      <i key={i} className="ri-star-fill text-amber-500 text-sm" />
    ))}
  </div>
);

const TestimonialsSection = () => {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section id="testimonios" className="py-28 bg-white w-full">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={titleRef}
          className={`text-center max-w-2xl mx-auto mb-20 scroll-hidden ${titleVisible ? 'scroll-visible' : ''}`}
        >
          <span className="font-body text-xs font-semibold tracking-widest text-amber-700 uppercase block mb-4">
            Lo que dicen
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-light text-stone-900 leading-tight mb-5">
            Miles de personas ya
            <br />
            <em className="not-italic font-semibold">viven con HELIX.</em>
          </h2>
          <p className="font-body text-stone-500 text-base">
            De médicos a atletas, de ejecutivos a padres. HELIX se adapta
            a quien eres y a lo que necesitas saber.
          </p>
        </div>

        {/* Aggregate score */}
        <div className={`flex items-center justify-center gap-8 mb-14 scroll-hidden ${titleVisible ? 'scroll-visible delay-200' : ''}`}>
          <div className="text-center">
            <div className="font-display text-5xl font-semibold text-stone-900">4.9</div>
            <StarRating count={5} />
            <div className="font-body text-xs text-stone-400 mt-1">de 5 estrellas</div>
          </div>
          <div className="w-px h-16 bg-stone-200" />
          <div className="text-center">
            <div className="font-display text-5xl font-semibold text-stone-900">12K+</div>
            <div className="font-body text-xs text-stone-400 mt-2">usuarios activos</div>
          </div>
          <div className="w-px h-16 bg-stone-200" />
          <div className="text-center">
            <div className="font-display text-5xl font-semibold text-stone-900">98%</div>
            <div className="font-body text-xs text-stone-400 mt-2">recomendarían HELIX</div>
          </div>
        </div>

        {/* Cards */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className={`p-8 rounded-2xl border border-stone-100 hover:border-amber-200 transition-all duration-300 scroll-hidden ${
                cardsVisible ? 'scroll-visible' : ''
              }`}
              style={{ transitionDelay: cardsVisible ? `${i * 0.12}s` : '0s' }}
            >
              <div className="flex items-start gap-4 mb-5">
                <img
                  src={t.avatar}
                  alt={t.name}
                  title={`${t.name} - testimonial HELIX Ring`}
                  className="w-14 h-14 rounded-full object-cover object-top flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="font-body font-semibold text-stone-900 text-sm">{t.name}</div>
                  <div className="font-body text-xs text-stone-400 mt-0.5">{t.role}</div>
                  <StarRating count={t.rating} />
                </div>
              </div>

              <p className="font-body text-stone-600 text-sm leading-relaxed mb-5 italic">
                &quot;{t.text}&quot;
              </p>

              <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 font-body text-xs font-medium px-3 py-1.5 rounded-full border border-emerald-200">
                <i className="ri-check-line" />
                {t.highlight}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
